import { Injectable } from '@angular/core';
import { AppInjector } from '../injector/injector.module';
import { Observable, forkJoin, of, mergeMap, firstValueFrom } from 'rxjs';
import { BaseEntitiesResponse, BaseResponse, CompanyInfo, GenericListEntity } from '@base/entities/BaseEntities';
import { EnumSystemOptions } from './sysenums.enum';
import { SysGlobals } from './sysglobals';
import { M4UCities } from '../../common/Cities';
import config from 'devextreme/core/config';
import { loadMessages, locale } from "devextreme/localization";
import { ResourcesService } from './resources.service';
import { ResourcesDEVX_HE } from '../../_resources/DEVX.he';
import { ResourcesDEVX_ES } from '../../_resources/DEVX.es';
import { DevExpressService } from './devexpress.service';
import { SystemEndpoint } from '../endpoints/system.endpoint';
import { SystemOptionsEndpoint } from '../endpoints/system-options.endpoint';
import { NavigatorService } from './navigator.service';
import { SessionService } from './session.service';
import { AppConfigService } from '@configuration/app.config.service';
import { EnumLanguages } from './sysenums.enum';
import { SystemOptionsService } from './system-options.service';
import {UiService} from '@core/framework/ui.service';
import { SysEventsService } from './sysevents.service';
import { DefaultRouteParams } from '@m4u-angular/base/classes/BaseClasses';
import { LocalStorageService } from './local-storage.service';

@Injectable()
export class FrameworkService {
    constructor(
        public systemEndpoint: SystemEndpoint,
        public systemOptionsEndpoint: SystemOptionsEndpoint,
        private resources: ResourcesService,
        private devExpress: DevExpressService,
        private appConfigService: AppConfigService,
        private sessionService: SessionService,
        private systemOptionsService: SystemOptionsService,
        private navigator: NavigatorService,
        private ui: UiService,
        private localStorageService: LocalStorageService,
        private sysEventsService: SysEventsService) { }

    //Variables
    private m_CompanyInfoDict: { [language: string]: CompanyInfo } = {};
    private m_bIsFrameworkInitialized: boolean;

    //Checks whether currently building this custom build
    public IsCustomBuild(build: string): boolean {
        return (this.appConfigService.M4UConfig.customBuild == build ? true : false);
    }

    //Shortcut to system options so that Framework is the only one that needs to be DI
    public SystemOption(OptionName: EnumSystemOptions | string): any {
        return this.systemOptionsService.getSystemOption(OptionName);
    }

    //Shortcut to system options so that Framework is the only one that needs to be DI
    public SystemOptionFlag(OptionName: EnumSystemOptions | string): boolean {
        return this.systemOptionsService.getSystemOptionAsBoolean(OptionName);
    }

    //Shortcut to system options so that Framework is the only one that needs to be DI
    public get CompanyInfo(): CompanyInfo {
        return this.m_CompanyInfoDict[SysGlobals.Language];
    }

    public IsFrameworkInitialized(): boolean {
        return this.m_bIsFrameworkInitialized;
    }

    public Do(serviceResponse: Observable<BaseResponse | Blob>, bShowSuccessMessage: boolean = true, after?: (afterResponse: BaseResponse | Blob) => void, fail?: (failResponse: BaseResponse | Blob) => void) {
        serviceResponse.subscribe((response: BaseResponse | Blob) => {
            if (response instanceof Blob) {
                after(response);
            }
            else {
                const baseResponse = response as BaseResponse;
                if (baseResponse.IsSuccess) {
                    if (bShowSuccessMessage) this.ui.InfoMessage(this.resources.RC("msgSuccess"));
                    if (after != undefined) after(response);
                }
                else {
                    if (fail != undefined) {
                        fail(response);
                    }
                    else {
                        if (baseResponse.ResponseCode == 999) {
                            this.ui.UnexpectedMessage(baseResponse.ResponseMessage);
                        } else {
                            this.ui.StopMessage(baseResponse.ResponseMessage);
                        }
                    }
                }
            }
        });
    }

    //Initialize the framework
    InitializeFramework(resolve): any {
        //Dispatch 'frameworkInitStarted' event
        //Syntax was adapted to work in the IE browser
        var event = document.createEvent('Event');
        event.initEvent('frameworkInitStarted', true, true);
        document.dispatchEvent(event);
        //Init company info
        forkJoin
            (
                [this.systemEndpoint.GetCompanyInfo(),
                this.systemEndpoint.GetSystemOptions(this.GetFirstModuleName())]
            ).subscribe(([responseCompanyInfo, responseSystemOptions]) => {
                     //Map company info
                     let companyInfo;
                     if (responseCompanyInfo.IsSuccess) {
                         companyInfo = responseCompanyInfo.Entity;
                     }
                     else {
                         this.ui.UnexpectedMessage(`Unable to load company info. Please reload the application ${responseCompanyInfo.ResponseMessage}`);
     
                         //Load some defaults, so app can continue at least to show the login screen or work in debug mode, 
                         //where boot process is not stopped at login screen if there are no settings
                         companyInfo = new CompanyInfo;
                         companyInfo.DecimalSeparator = ".";
                         companyInfo.ThousandsSeparator = ",";
                         companyInfo.DateFormatLong = "dd/MM/yyyy";
                     }
                     this.InitCompanyInfoDictinary(companyInfo);
     
                     //Map system options
                     if (responseSystemOptions.IsSuccess) {
                         this.systemOptionsService.setOptions(responseSystemOptions.Entities);
                     }
                     else {
                         this.ui.UnexpectedMessage(`Unable to load system options. Please reload the application ${responseSystemOptions.ResponseMessage}`);
                     }
     
                     //Initialize language
                     this.InitLanguage()
     
                     //Initialize devexpress
                     this.InitializeDevExpress();
     
                     // Initalize injectable components options
                     this.InitializeInjectableComponentsOptions();
     
                     //Signal we can continue
                     this.m_bIsFrameworkInitialized = responseCompanyInfo.IsSuccess && responseSystemOptions.IsSuccess;
                     resolve();
                 });

    }

    public InitializeDevExpress() {
        this.devExpress.InitializeDevExpress(this);
    }

    //Handle language change
    public InitLanguage() {
        //Set default
        //Check in local storage
        try {
            if (SysGlobals.Language == undefined) SysGlobals.Language = this.localStorageService.GetItem("Language");
        } catch (e) { }
        if (SysGlobals.Language == undefined) SysGlobals.Language = this.appConfigService.M4UConfig.defaultLanguage;

        //Initialize DevExpress translation messages
        if (SysGlobals.Language == EnumLanguages.Hebrew) loadMessages(ResourcesDEVX_HE);
        if (SysGlobals.Language == EnumLanguages.Spanish) loadMessages(ResourcesDEVX_ES);
        locale(SysGlobals.Language.toLowerCase());

        //Initialize devexpress stuff
        config(
            {
                rtlEnabled: SysGlobals.Language == EnumLanguages.Hebrew ? true : false,
                forceIsoDateParsing: true
            });

        //Init resources
        this.resources.InitDictionaries();

    }

    InitCompanyInfoDictinary(companyInfo: CompanyInfo) {
        let companyInfoHebrew = { ...companyInfo };
        companyInfoHebrew.NumberGeneral = `${companyInfoHebrew.NumberGeneral};${companyInfoHebrew.NumberGeneral}-`;
        companyInfoHebrew.NumberPercent = `${companyInfoHebrew.NumberPercent};${companyInfoHebrew.NumberPercent}-`;
        companyInfoHebrew.NumberPrice = `${companyInfoHebrew.NumberPrice};${companyInfoHebrew.NumberPrice}-`;
        companyInfoHebrew.NumberQuantity = `${companyInfoHebrew.NumberQuantity};${companyInfoHebrew.NumberQuantity}-`;
        companyInfoHebrew.NumberRate = `${companyInfoHebrew.NumberRate};${companyInfoHebrew.NumberRate}-`;
        companyInfoHebrew.NumberSum = `${companyInfoHebrew.NumberSum};${companyInfoHebrew.NumberSum}-`;

        this.m_CompanyInfoDict[EnumLanguages.Hebrew] = companyInfoHebrew;
        this.m_CompanyInfoDict[EnumLanguages.English] = companyInfo;
        this.m_CompanyInfoDict[EnumLanguages.Spanish] = companyInfo;
    }

    GetCities(): Observable<any> {
        var oResult = new BaseEntitiesResponse;
        oResult.IsSuccess = true;
        oResult.Entities = M4UCities.map((element) => {
            return new GenericListEntity(element, element);
        });

        return of(oResult);
    }

    NavigateDefault(): Promise<boolean> {
        //SysGlobals.LastNavigationUrl = SysGlobals.LastNavigationParams = undefined;

        return firstValueFrom(
            this.appConfigService.AppConfig.OnGetDefaultRouteParams(this.sessionService.UserInfos[0])
                .pipe(
                    mergeMap(defaultRouteParams => {
                        if (!defaultRouteParams || !defaultRouteParams.Route) {
                            defaultRouteParams = new DefaultRouteParams;
                            defaultRouteParams.Route = '/system/login';
                        };

                        return this.navigator.Navigate([defaultRouteParams.Route], defaultRouteParams.Params).then((result) => {
                            this.sysEventsService.NavigateDefault(result);
                            return Promise.resolve(true);
                        });
                    })
                )
        );
    }

    private InitializeInjectableComponentsOptions() {
        this.appConfigService.AppConfig.GetInjectableComponentOptions().subscribe((options) => {
            SysGlobals.InjectableComponentsOptions = options;
        });
    }

    private GetFirstModuleName(): string {
        const moduleNames = this.appConfigService.AppConfig.ModuleName.split(',');
        return moduleNames[0];
    }
}

//Entry point for framework initialization
export function OnInitializeFramework(): () => Promise<any> {
    //Initialize stuff that require pulling from the server
    return (): Promise<any> => {
        return new Promise((resolve, reject) => {
            var service = AppInjector.get(FrameworkService);
            service.InitializeFramework(resolve);
        });
    };
}
